import axios from 'axios'
import { executeRecaptcha } from './recaptcha'

/**
 * Tenant portal API client.
 *
 * Third sibling to services/api.js (operator) and services/platformApi.js
 * (super-admin):
 *  - Different baseURL - /api/portal.
 *  - Different localStorage key - `tenant_token`, so tenant sessions don't
 *    collide with operator (`token`) or platform-admin (`platform_token`).
 *  - 401 redirects to /portal/login.
 */
const API_BASE = (import.meta.env.VITE_API_URL || '').replace(/\/+$/, '')

const portal = axios.create({
  baseURL: `${API_BASE}/api/portal`,
  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
})

portal.interceptors.request.use((config) => {
  const token = localStorage.getItem('tenant_token')
  if (token) config.headers.Authorization = `Bearer ${token}`
  return config
})

portal.interceptors.response.use(
  (res) => res,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('tenant_token')
      localStorage.removeItem('tenant_user')
      if (!window.location.pathname.startsWith('/portal/login')) {
        window.location.href = '/portal/login'
      }
    }
    return Promise.reject(error)
  },
)

export default portal

// ─── Auth (public) ─────────────────────────────────────────────────
// activate, login, forgot-password attach a v3 reCAPTCHA token bound
// to a portal-specific action so a token minted for the operator
// /login screen can't be replayed against /portal/login.
const withRecaptcha = async (action, data) => {
  const recaptcha_token = await executeRecaptcha(action)
  return { ...data, recaptcha_token }
}
export const portalAuthApi = {
  showToken:      (token)        => portal.get(`/tokens/${token}`),
  activate:       async (token, data) => portal.post(`/activate/${token}`,        await withRecaptcha('portal_activate',        data)),
  login:          async (data)        => portal.post('/login',                   await withRecaptcha('portal_login',           data)),
  forgotPassword: async (data)        => portal.post('/forgot-password',         await withRecaptcha('portal_forgot_password', data)),
  resetPassword:  (token, data)  => portal.post(`/reset-password/${token}`, data),
  logout:         ()             => portal.post('/logout'),
}

// ─── Me ────────────────────────────────────────────────────────────
export const portalMeApi = {
  show:           ()             => portal.get('/me'),
  update:         (data)         => portal.patch('/me', data),
  accessCodes:    ()             => portal.get('/access-codes'),
  lease:          (rentalId)     => portal.get(`/lease/${rentalId}`),
  toggleAutopay:  (enabled)      => portal.post('/autopay', { enabled }),
}

// ─── Invoices ──────────────────────────────────────────────────────
export const portalInvoicesApi = {
  list:           (params)       => portal.get('/invoices', { params }),
  get:            (id)           => portal.get(`/invoices/${id}`),
  pay:            (id)           => portal.post(`/invoices/${id}/pay`),
}

// ─── Payment method ────────────────────────────────────────────────
export const portalPaymentMethodApi = {
  show:           ()             => portal.get('/payment-method'),
  setupIntent:    ()             => portal.post('/payment-method/setup-intent'),
  store:          (data)         => portal.post('/payment-method', data),
  remove:         ()             => portal.delete('/payment-method'),
}

// ─── Move-out requests ─────────────────────────────────────────────
export const portalMoveOutApi = {
  list:           ()             => portal.get('/move-out-requests'),
  create:         (data)         => portal.post('/move-out-requests', data),
  cancel:         (id)           => portal.delete(`/move-out-requests/${id}`),
}

// ─── GDPR / CCPA data-subject rights (tenant) ─────────────────────
export const portalPrivacyApi = {
  exportData: async () => {
    const res = await portal.get('/me/data-export', { responseType: 'blob' })
    let filename = 'my-stovalt-data.zip'
    const disposition = res.headers['content-disposition']
    if (disposition) {
      const match = /filename="?([^"]+)"?/.exec(disposition)
      if (match) filename = match[1]
    }
    const url = window.URL.createObjectURL(res.data)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()
    window.URL.revokeObjectURL(url)
  },
  deletionStatus:  ()     => portal.get('/me/deletion-request'),
  requestDeletion: (data) => portal.post('/me/deletion-request', data),
  cancelDeletion:  ()     => portal.delete('/me/deletion-request'),
}
